/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import type { ReactNode } from 'react';

import { css, jsx } from '@compiled/react';

import {
	BrandHeading,
	Card,
	Carousel,
	type ContentCardData,
	ContentCardGroup,
	FadeIn,
	type GroupData,
	Hero,
	Image,
	LinkWithArrow,
	SlideAndFadeIn,
} from '@af/design-system-docs-ui';
import Heading from '@atlaskit/heading';
import AtlaskitImage from '@atlaskit/image';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { ContextualAnalyticsData, FireScreenAnalytics, SCREEN } from '@atlassian/analytics-bridge';

import { Bookend } from '../components/bookend';
import { HoverActivatedVideo } from '../components/hover-activated-video';

import atlassianUIRefresh from './_assets/atlassian-ui-refresh.png';
import buttonEvolutionMP4 from './_assets/button-evolution.mp4';
import buttonEvolutionPNG from './_assets/button-evolution.png';
import contentDesigner from './_assets/content-designer.png';
import designer from './_assets/designer.png';
import developer from './_assets/developer.png';
import foundationsCarouselMobilePanel1 from './_assets/foundations-carousel-mobile-panel1.png';
import foundationsCarouselMobilePanel2 from './_assets/foundations-carousel-mobile-panel2.png';
import foundationsCarouselMobilePanel3 from './_assets/foundations-carousel-mobile-panel3.png';
import foundationsCarouselPanel1 from './_assets/foundations-carousel-panel1.png';
import foundationsCarouselPanel2 from './_assets/foundations-carousel-panel2.png';
import foundationsCarouselPanel3 from './_assets/foundations-carousel-panel3.png';
import harmoniousCarouselMobilePanel1 from './_assets/harmonious-carousel-mobile-panel1.png';
import harmoniousCarouselMobilePanel2 from './_assets/harmonious-carousel-mobile-panel2.png';
import harmoniousCarouselMobilePanel3 from './_assets/harmonious-carousel-mobile-panel3.png';
import harmoniousCarouselMobilePanel4 from './_assets/harmonious-carousel-mobile-panel4.png';
import harmoniousCarouselPanel1 from './_assets/harmonious-carousel-panel1.png';
import harmoniousCarouselPanel2 from './_assets/harmonious-carousel-panel2.png';
import harmoniousCarouselPanel3 from './_assets/harmonious-carousel-panel3.png';
import harmoniousCarouselPanel4 from './_assets/harmonious-carousel-panel4.png';
import componentImageMap from './components/_assets/_map';
import { homePageDesignAtAtlassianCardData } from './design-at-atlassian/_content';

const guidelineCardData: GroupData<ContentCardData>[] = [
	{
		title: 'Foundational guidelines and visual styles',
		cards: [
			{
				title: 'Tokens',
				description: 'Single sources of truth to name and store design decisions.',
				href: '/foundations/tokens',
				icon: 'tokens',
			},
			{
				title: 'Accessibility',
				description: 'Enable everyone to interact with, understand, and navigate our apps.',
				href: '/foundations/accessibility',
				icon: 'accessibility',
			},
			{
				title: 'Content',
				description:
					'Clear, concise, and conversational language to get teams where they need to go.',
				href: '/foundations/content',
				icon: 'content',
			},
			{
				title: 'Color',
				description: 'Distinguish our brand and reinforce consistent experiences across apps.',
				href: '/foundations/color-new',
				icon: 'color',
			},
			{
				title: 'Typography',
				description: "Enhance communication, reinforce brand, and guide users' emotions.",
				href: '/foundations/typography',
				icon: 'typography',
			},
			{
				title: 'Spacing',
				description: 'Simplify the creation of page layouts and UI.',
				href: '/foundations/spacing',
				icon: 'spacing',
			},
		],
	},
];

const componentCardData: GroupData<ContentCardData>[] = [
	{
		title: 'Flexible components and primitives',
		cards: [
			{
				title: 'Button',
				description:
					'A button triggers an event or action. They let users know what will happen next.',
				href: '/components/button',
				image: componentImageMap['button'],
			},
			{
				title: 'Badge',
				description: 'A badge is a visual indicator for numeric values such as tallies and scores.',
				href: '/components/badge',
				image: componentImageMap['badge'],
			},
			{
				title: 'Flag',
				description:
					'A flag is used for confirmations, alerts, and acknowledgments that require minimal user interaction, often displayed using a flag group.',
				href: '/components/flag',
				image: componentImageMap['flag'],
			},
			{
				title: 'Box',
				description:
					'A box is a primitive component that acts as a generic container, and provides managed access to design tokens.',
				href: '/components/primitives/box',
				image: componentImageMap['box'],
			},
			{
				title: 'Inline',
				description:
					'Inline is a primitive component based on flexbox that manages the horizontal layout of direct children.',
				href: '/components/primitives/inline',
				image: componentImageMap['inline'],
			},
			{
				title: 'Stack',
				description:
					'Stack is a primitive component based on flexbox that manages the vertical layout of direct children.',
				href: '/components/primitives/stack',
				image: componentImageMap['stack'],
			},
		],
	},
];

const toolsCardData: GroupData<ContentCardData>[] = [
	{
		title: 'Tools for designers and developers',
		cards: [
			{
				title: 'Figma libraries',
				description: 'Our tokens, foundations, components, and plugins.',
				href: '/get-started/figma-libraries',
				icon: 'figma',
			},
			{
				title: 'ESlint plugin',
				description: 'The essential linting plugin for Atlassian Design System.',
				href: '/components/eslint-plugin-design-system',
				icon: 'eslintPlugin',
			},
			{
				title: 'UI Styling Standard',
				description: "Principles to guide styling UI code across Atlassian's frontend.",
				href: '/components/eslint-plugin-ui-styling-standard',
				icon: 'ui-styling-standard',
			},
		],
	},
];

const stackStyles = xcss({ height: '100%' });
const getStartedCardStyles = xcss({
	height: '100%',
});
const mainContainerStyles = xcss({
	gridArea: 'content / content / nav / nav',
});

const mainContentStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.1000',
	minWidth: '320px',
	maxWidth: '1440px',
	paddingBlockEnd: 'space.1000',
	paddingInline: 'space.150',
	'@media (min-width: 30rem)': {
		paddingInline: 'space.300',
	},
});

const latestReleaseImageStyles = css({
	height: '100%',
	objectFit: 'cover',
});

const Section = ({ children }: { children: ReactNode }) => (
	<Stack space="space.200">{children}</Stack>
);

const gridStyles = xcss({
	display: 'grid',
	gap: 'space.100',
	gridTemplateColumns: '1fr',
	'@media (min-width: 30rem)': {
		gridTemplateColumns: '1fr 1fr 1fr',
	},
});

const Grid = ({ children }: { children: ReactNode }) => {
	return <Box xcss={gridStyles}>{children}</Box>;
};

const highlightGridStyles = xcss({
	display: 'grid',
	gridTemplateColumns: '1fr',
	'@media (min-width: 48rem)': {
		gridTemplateColumns: '4fr 2fr',
	},
	borderRadius: '12px',
	overflow: 'hidden',
});

const HighlightGrid = ({ children }: { children: ReactNode }) => {
	return <Box xcss={highlightGridStyles}>{children}</Box>;
};

const paddedContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
	'@media (min-width: 48rem)': {
		paddingInline: 'space.1000',
	},
});

const PaddedContainer = ({ children }: { children: ReactNode }) => {
	return <Box xcss={paddedContainerStyles}>{children}</Box>;
};

export default () => {
	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="home">
			<FireScreenAnalytics />
			<Box xcss={mainContainerStyles}>
				<FadeIn>
					<Hero
						title={
							<BrandHeading size="hero" as="h1">
								Design with clarity
								<br />
								Build with confidence
							</BrandHeading>
						}
						subtitle={
							<Text size="large">
								Ship Atlassian interfaces using quality foundations, flexible components, and
								powerful tools.
							</Text>
						}
					/>
				</FadeIn>

				<Inline alignInline="center">
					<Box as="main" xcss={mainContentStyles}>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<BrandHeading as="h2" size="xlarge">
										Get started
									</BrandHeading>
									<Grid>
										<SlideAndFadeIn delay="0ms">
											<Card href="/get-started/design" appearance="yellow">
												<Stack space="space.200" xcss={getStartedCardStyles}>
													<Inline alignInline="center">
														<Box>
															<Image src={designer} alt="" />
														</Box>
													</Inline>
													<Box padding="space.200">
														<Stack space="space.050">
															<Heading as="h3" size="small">
																For designers
															</Heading>
															<Text>Design guidelines, Figma libraries, plugins, and tools.</Text>
														</Stack>
													</Box>
												</Stack>
											</Card>
										</SlideAndFadeIn>

										<SlideAndFadeIn delay="200ms">
											<Card href="/get-started/develop" appearance="purple">
												<Stack space="space.200" xcss={getStartedCardStyles}>
													<Inline alignInline="center">
														<Box>
															<Image src={developer} alt="" />
														</Box>
													</Inline>
													<Box padding="space.200">
														<Stack space="space.050">
															<Heading as="h3" size="small">
																For developers
															</Heading>
															<Text>
																Environment configuration, tooling, and composition guides.
															</Text>
														</Stack>
													</Box>
												</Stack>
											</Card>
										</SlideAndFadeIn>

										<SlideAndFadeIn delay="400ms">
											<Card href="/get-started/content-design" appearance="blue">
												<Stack space="space.200" xcss={getStartedCardStyles}>
													<Inline alignInline="center">
														<Box>
															<Image src={contentDesigner} alt="" />
														</Box>
													</Inline>
													<Box padding="space.200">
														<Stack space="space.050">
															<Heading as="h3" size="small">
																For content designers
															</Heading>
															<Text>Content standards, voice and tone, and design assets.</Text>
														</Stack>
													</Box>
												</Stack>
											</Card>
										</SlideAndFadeIn>
									</Grid>

									<LinkWithArrow href="/get-started">Explore all get started guides</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>

						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<BrandHeading as="h2" size="xlarge">
										Latest releases
									</BrandHeading>
								</PaddedContainer>

								<HighlightGrid>
									<AtlaskitImage src={atlassianUIRefresh} alt="" css={latestReleaseImageStyles} />
									<Box backgroundColor="color.background.neutral.bold" padding="space.400">
										<Stack xcss={stackStyles} space="space.200" alignBlock="center">
											<Stack space="space.100">
												<BrandHeading as="h3" color="color.text.inverse" size="large">
													Atlassian UI refresh updates
												</BrandHeading>
												<Text color="color.text.inverse">
													How to apply Atlassian’s bold new visual style with the design system.
												</Text>
											</Stack>
											<LinkWithArrow
												appearance="inverse"
												href="/whats-new/atlassian-ui-refresh-updates"
											>
												Read release roundup
											</LinkWithArrow>
										</Stack>
									</Box>
								</HighlightGrid>
								<PaddedContainer>
									<LinkWithArrow href="/whats-new">Explore what's new</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<BrandHeading as="h2" size="xlarge">
										Design with elegant foundations
									</BrandHeading>
								</PaddedContainer>
								<Carousel
									name="foundations-carousel"
									panels={[
										{
											content: (
												<Image
													src={foundationsCarouselPanel1}
													srcMobile={foundationsCarouselMobilePanel1}
													alt=""
												/>
											),
											description:
												'Design decisions are expressed with tokens and modular building blocks.',
										},
										{
											content: (
												<Image
													src={foundationsCarouselPanel2}
													srcMobile={foundationsCarouselMobilePanel2}
													alt=""
												/>
											),
											description: 'Foundations combine to create harmonious components.',
										},
										{
											content: (
												<Image
													src={foundationsCarouselPanel3}
													srcMobile={foundationsCarouselMobilePanel3}
													alt=""
												/>
											),
											description:
												'Every part of the system supports accessibility and consistency.',
										},
									]}
								/>
							</Section>
						</SlideAndFadeIn>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<ContentCardGroup data={guidelineCardData} />
									<LinkWithArrow href="/foundations">Explore all foundations</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<BrandHeading as="h2" size="xlarge">
										Build iconic Atlassian experiences
									</BrandHeading>
								</PaddedContainer>

								<Carousel
									name="foundations-carousel"
									panels={[
										{
											content: (
												<Image
													src={harmoniousCarouselPanel1}
													srcMobile={harmoniousCarouselMobilePanel1}
													alt=""
												/>
											),
											description: 'Access modular and responsive components.',
										},
										{
											content: (
												<Image
													src={harmoniousCarouselPanel2}
													srcMobile={harmoniousCarouselMobilePanel2}
													alt=""
												/>
											),
											description: 'Layer components to build consistent patterns.',
										},
										{
											content: (
												<Image
													src={harmoniousCarouselPanel3}
													srcMobile={harmoniousCarouselMobilePanel3}
													alt=""
												/>
											),
											description: 'Create collaborative experiences for all teams.',
										},
										{
											content: (
												<Image
													src={harmoniousCarouselPanel4}
													srcMobile={harmoniousCarouselMobilePanel4}
													alt=""
												/>
											),
											description: 'Build consistent journeys across apps.',
										},
									]}
								/>
							</Section>
						</SlideAndFadeIn>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<ContentCardGroup data={componentCardData} />
									<LinkWithArrow href="/components">Explore all components</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>

						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<BrandHeading as="h2" size="xlarge">
										Ship quickly as a team
									</BrandHeading>
								</PaddedContainer>

								<HighlightGrid>
									<HoverActivatedVideo poster={buttonEvolutionPNG} src={buttonEvolutionMP4} />
									<Box backgroundColor="color.background.neutral" padding="space.400">
										<Stack xcss={stackStyles} space="space.200" alignBlock="center">
											<Stack space="space.100">
												<BrandHeading as="h3" color="color.text" size="large">
													Evolving buttons and links
												</BrandHeading>
												<Text color="color.text">
													A closer look at how we've revamped our button and link components to be
													more accessible, performant, and consistent.
												</Text>
											</Stack>
											<LinkWithArrow href="/whats-new/evolving-button-and-links">
												Read feature deep dive
											</LinkWithArrow>
										</Stack>
									</Box>
								</HighlightGrid>

								<PaddedContainer>
									<LinkWithArrow href="/whats-new">Explore what's new</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<ContentCardGroup data={toolsCardData} />
									<LinkWithArrow href="/tools">Explore all tools</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>
						<SlideAndFadeIn>
							<Section>
								<PaddedContainer>
									<ContentCardGroup data={homePageDesignAtAtlassianCardData} />
									<LinkWithArrow href="/design-at-atlassian">Explore design news</LinkWithArrow>
								</PaddedContainer>
							</Section>
						</SlideAndFadeIn>
					</Box>
				</Inline>
				<Section>
					<Bookend variant="get-started">
						<Box paddingBlock="space.1000" paddingInlineStart="space.1000">
							<Stack space="space.100">
								<BrandHeading as="h2" size="xxlarge" color="color.text.inverse">
									Get started
								</BrandHeading>
								<Stack alignInline="start" space="space.150">
									<Text size="large" color="color.text.inverse">
										Join thousands of designers and developers already using the Atlassian Design
										System.
									</Text>
									<LinkWithArrow href="/get-started" appearance="inverse">
										Explore get started guides
									</LinkWithArrow>
								</Stack>
							</Stack>
						</Box>
					</Bookend>
				</Section>
			</Box>
		</ContextualAnalyticsData>
	);
};
